import React from 'react';
import { Download } from 'lucide-react';
import {
  canOpenClientDetail,
  canOpenProjectDetail,
  canOpenUserDetail,
} from '../lib/detailAccess.js';
import { HoursBreakdown } from './HoursBreakdown.jsx';
import { hoursFromReport } from '../lib/workTypes.js';
import { useSessionState } from '../lib/useSessionState.js';
import {
  ClientsReport,
  EntriesReport,
  PeopleReport,
  ProjectsReport,
  SummaryReport,
} from './reporting/ReportTables.jsx';
import { ReportFilters } from './reporting/ReportFilters.jsx';
import { ReportBreakdownCharts } from './reporting/ReportCharts.jsx';
import {
  useReportData,
  useReportFilters,
} from './reporting/useReportFilters.js';
import { PageHeader } from './PageSkeleton.jsx';
import {
  exportClients,
  exportEntries,
  exportOverview,
  exportPeople,
  exportProjects,
} from '../lib/exportExcel.js';

export function ReportView({
  data,
  mode = 'all',
  onOpenClient,
  onOpenProject,
  onOpenUser,
}) {
  const isMyReport = mode === 'mine';
  const persistKey = isMyReport ? 'my-report' : 'reports';
  const [storedTab, setTab] = useSessionState(`${persistKey}.tab`, 'overview');
  const filters = useReportFilters(data, { mode, persistKey });
  const { report, loading } = useReportData(filters.query);
  const { from, to } = filters;

  const summary = report.summary;
  const peopleRows = report.people;
  const projectRows = report.projects;
  const clientRows = report.clients;

  const tabs = isMyReport
    ? [
        ['overview', 'Overview'],
        ['clients', 'By client'],
        ['projects', 'By project'],
        ['entries', 'Raw entries'],
      ]
    : [
        ['overview', 'Overview'],
        ['clients', 'By client'],
        ['projects', 'By project'],
        ['people', 'By person'],
        ['entries', 'Raw entries'],
      ];
  const tab = tabs.some(([key]) => key === storedTab) ? storedTab : 'overview';

  function handleExport() {
    const range = { from, to };
    if (tab === 'entries')
      exportEntries(report.entries, data.allProjects, range);
    else if (tab === 'projects') exportProjects(projectRows, range);
    else if (tab === 'clients') exportClients(clientRows, range);
    else if (tab === 'people') exportPeople(peopleRows, range);
    else exportOverview(summary, projectRows, clientRows, peopleRows, range);
  }

  return (
    <div>
      <PageHeader
        title={isMyReport ? 'My report' : 'Reports'}
        subtitle={`${from} to ${to} · ${summary.entryCount} entries${loading ? ' · Loading...' : ''}`}
      />

      <HoursBreakdown
        hours={hoursFromReport(summary)}
        totalLabel={isMyReport ? 'My total' : 'Total'}
      />

      <ReportFilters
        data={data}
        isMyReport={isMyReport}
        workTypeHours={summary.workTypeHours}
        {...filters}
      />

      <div className="tabs-row">
        <div className="tabs">
          {tabs.map(([key, label]) => (
            <button
              key={key}
              className={tab === key ? 'active' : ''}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          className="secondary small tab-action"
          onClick={handleExport}
          disabled={loading}
        >
          <Download size={14} /> Export
        </button>
      </div>

      {tab === 'overview' && (
        <>
          {!isMyReport && <ReportBreakdownCharts report={report} />}
          <SummaryReport
            summary={summary}
            projectRows={projectRows}
            clientRows={clientRows}
            peopleRows={peopleRows}
          />
        </>
      )}
      {tab === 'projects' && (
        <ProjectsReport
          rows={projectRows}
          total={summary.totalHours}
          onOpenProject={onOpenProject}
          canOpenProject={(id) => canOpenProjectDetail(data, id)}
          persistKey={persistKey}
        />
      )}
      {tab === 'clients' && (
        <ClientsReport
          rows={clientRows}
          projectRows={projectRows}
          total={summary.totalHours}
          onOpenClient={onOpenClient}
          canOpenClient={(id) => canOpenClientDetail(data, id)}
          persistKey={persistKey}
        />
      )}
      {!isMyReport && tab === 'people' && (
        <PeopleReport
          rows={peopleRows}
          total={summary.totalHours}
          onOpenUser={onOpenUser}
          canOpenUser={(id) => canOpenUserDetail(data, id)}
          persistKey={persistKey}
        />
      )}
      {tab === 'entries' && (
        <EntriesReport
          rows={report.entries}
          data={data}
          persistKey={persistKey}
        />
      )}
    </div>
  );
}
